import { useQuery } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { Button, Input, Select } from '../../../custom';
import { useCreateUpdateRequiredDocumentMutation } from '../../../hooks/api/useCreateUpdateRequiredDocumentMutation';

import { getProgramTypes } from '../programType/request';

interface RequiredDocumentFormProps {
  handleClose: () => void;
  record?: RequiredDocument | null;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Document name is required'),
  maxFileSizeInMB: Yup.number()
    .typeError('Max file size is required')
    .min(0, 'Max file size cannot be negative')
    .required('Max file size is required'),
  allowedFileTypes: Yup.string().required('Allowed file types are required'),
  isRequired: Yup.string().required('Required status is required'),
  programType: Yup.string().required('Program type is required'),
  description: Yup.string().required('Description is required'),
});

const requiredOptions = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

const RequiredDocumentForm = ({ handleClose, record }: RequiredDocumentFormProps) => {
  const { data: programTypeResponse, isLoading: isProgramTypesLoading } = useQuery({
    queryKey: ['get-program-types'],
    queryFn: getProgramTypes,
  });

  const { createUpdateRequiredDocumentHandler, isLoading } = useCreateUpdateRequiredDocumentMutation(handleClose);

  const programTypes = programTypeResponse?.data ?? [];

  const programTypeOptions = programTypes?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.name}
    </option>
  ));

  return (
    <Formik
      initialValues={{
        id: record?.id ?? 0,
        name: record?.name ?? '',
        maxFileSizeInMB: record?.maxFileSizeInMB ?? '',
        allowedFileTypes: record?.allowedFileTypes ?? '',
        isRequired: String(record?.isRequired ?? ''),
        programType: String(record?.programTypeId ?? ''),
        description: record?.description ?? '',
      }}
      onSubmit={(values, { resetForm }) => {
        createUpdateRequiredDocumentHandler(values, resetForm);
      }}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {() => (
        <Form className="fields">
          <Input name="name" label="Document Name" placeholder="Enter document name" />

          <Input
            name="maxFileSizeInMB"
            label="Max File Size (MB)"
            placeholder="Enter max file size"
            type="number"
            min={0}
          />

          <Input name="allowedFileTypes" label="Allowed File Types" placeholder="e.g. pdf,jpg,png" />

          <Select
            name="programType"
            label="Program Type"
            placeholder="Select program type"
            options={programTypeOptions}
            loading={isProgramTypesLoading}
          />

          <Select
            name="isRequired"
            label="Is Required"
            placeholder="Select required status"
            options={requiredOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          />

          <Input name="description" label="Description" placeholder="Enter description" type="textarea" />

          <div className="btn-group">
            <Button type="button" onClick={handleClose} variant="text" text="Cancel" />
            <Button
              text={isLoading ? 'Submitting...' : record?.id ? 'Update' : 'Submit'}
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default RequiredDocumentForm;
